import { Grid } from '@mui/material';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import { useSnackbar } from 'notistack';
import * as React from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    useCreateListDataMutation,
    useUpdateListDataMutation,
} from '../../api/apiSlice';
import ExternalScreen from '../../components/fragments/ExternalScreen';
import { setUser } from '../../store/slices/authSlice';
import errorSnackbar from '../../utils/errorSnackbar';
import Confirm from './minister/Confirm';
import Payment from './minister/Payment';
import Update from './minister/Update';

const steps = ['Profile Update', 'Payment', 'Confirmation'];
const amountToPay = 200;

export default function MinisterSignupPage() {
    const [searchParams] = useSearchParams();

    const paramPage = searchParams.get('page');

    const [activeStep, setActiveStep] = useState(parseInt(paramPage) || 0);
    const [skipped, setSkipped] = useState(new Set());
    const [fieldData, setFieldData] = useState({});
    const [paymentId, setPaymentId] = useState(null);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const {
        auth: { user },
    } = useSelector((st) => st);
    const [payDPO, { isLoading }] = useCreateListDataMutation();
    const [renewSub] = useCreateListDataMutation();
    const [confirmPayment, { isLoading: isConfirmLoading }] =
        useUpdateListDataMutation();

    const { enqueueSnackbar } = useSnackbar();

    const renewSubscription = async () => {
        const { data, error } = await renewSub({ list: '/pastors/renew' });

        errorSnackbar(error, enqueueSnackbar);

        if (data) {
            enqueueSnackbar('Subscription Renewed', { variant: 'info' });
        }
    };

    const onPayment = async (vals) => {
        const { data, error } = await payDPO({
            list: '/wallet/dpo/mpesa',
            body: { ...vals, amount: amountToPay },
        });

        errorSnackbar(error, enqueueSnackbar);

        if (data) {
            enqueueSnackbar('Payment Initiated', { variant: 'info' });
            setPaymentId(data?.data?.id);
            setActiveStepAs(2);
        }
    };

    const onConfirm = async () => {
        const { data, error } = await confirmPayment({
            list: `/wallet/dpo/mpesa/${paymentId}`,
        });

        errorSnackbar(error, enqueueSnackbar);

        if (data) {
            data?.data?.status == 'undetermined' &&
                enqueueSnackbar('Payment Not Found', { variant: 'error' });

            if (data?.data?.status == 'completed') {
                enqueueSnackbar('Payment Confirmed');
                await renewSubscription();
                dispatch(
                    setUser({
                        ...user,
                        status: { ...user.status, pastor: true },
                    })
                );
                navigate(-1);
            }
        }
    };

    const isStepSkipped = (step) => {
        return skipped.has(step);
    };

    const handleNext = (data) => {
        let newSkipped = skipped;
        if (isStepSkipped(activeStep)) {
            newSkipped = new Set(newSkipped.values());
            newSkipped.delete(activeStep);
        }
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setSkipped(newSkipped);
        setFieldData({ ...fieldData, ...data });
    };

    const setActiveStepAs = (step) => {
        setActiveStep(step);
    };

    return (
        <ExternalScreen title="Online Minister" authPass>
            <Grid item xs={12}>
                <Stepper
                    sx={{ my: 2 }}
                    alternativeLabel
                    activeStep={activeStep}
                >
                    {steps.map((label, index) => {
                        const stepProps = {};
                        const labelProps = {};
                        if (isStepSkipped(index)) {
                            stepProps.completed = false;
                        }
                        return (
                            <Step key={label} {...stepProps}>
                                <StepLabel {...labelProps}>{label}</StepLabel>
                            </Step>
                        );
                    })}
                </Stepper>
                <React.Fragment>
                    {activeStep === 0 && (
                        <Update
                            handleNext={handleNext}
                            setActiveStepAs={setActiveStepAs}
                        />
                    )}
                    {activeStep === 1 && (
                        <Payment
                            onPayment={onPayment}
                            amountToPay={amountToPay}
                            isLoading={isLoading}
                        />
                    )}
                    {activeStep === 2 && (
                        <Confirm
                            onConfirm={onConfirm}
                            amountToPay={amountToPay}
                            isLoading={isConfirmLoading}
                        />
                    )}
                </React.Fragment>
            </Grid>
        </ExternalScreen>
    );
}
